<template>
  <BarChart
    :request-status="requestStatus"
    :series="series"
    :categories="categories"
    :title="title"
    :item-id="itemId"
    :card-options="cardOptions"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import BarChart from '@/components/cards/models/BarChart';
import { mapActions } from 'vuex';
import { ENTITIES, OPERATION, USERS_FIELDS } from '@/services/charts-service';
import cardsModel from '@/mixins/cards-model';
import highchartsBasics from '@/mixins/highcharts-basics';
import { formatTitleAndChannel } from '@/utils/channels';

const parseCategories = (categories) => ({ coordinates }) => {
  const category = formatTitleAndChannel(coordinates);

  if (!categories.includes(category)) {
    categories.push(category);
  }
};
const findCategory = (category) => ({ coordinates }) => category === formatTitleAndChannel(coordinates);

export default {
  name: 'VariationOfFanBaseChart',
  components: {
    BarChart,
  },
  mixins: [
    cardsModel,
    highchartsBasics,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    isExpandedCard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawData: {},
      cardOptions: {
        colors: ['#48B2FF', '#99ebe5'],
        xAxis: {},
        tooltip: {
          valueSuffix: ' fãs',
        },
        legend: {
          enabled: true,
        },
        plotOptions: {
          bar: {
            dataLabels: {
              enabled: true,
            },
          },
        },
        series: this.series,
      },
    };
  },
  computed: {
    currentPeriodPoints() {
      return this.rawData.currentPeriod?.points;
    },
    displacedPeriodPoints() {
      return this.rawData.displacedPeriod?.points;
    },
    series() {
      if (!this.currentPeriodPoints || !this.displacedPeriodPoints) {
        return [];
      }

      const currentData = [];
      const displacedData = [];

      this.categories.forEach((category, index) => {
        if (index >= this.maxItems() && !this.isExpandedCard) {
          return;
        }

        const findHandler = findCategory(category);
        currentData.push(this.currentPeriodPoints.find(findHandler)?.value || null);
        displacedData.push(this.displacedPeriodPoints.find(findHandler)?.value || null);
      });

      return [{
        name: this.$t('components.VariationOfFanBaseChart.currentPeriod'),
        data: currentData,
      },
      {
        name: this.$t('components.VariationOfFanBaseChart.displacedPeriod'),
        data: displacedData,
      }];
    },
    categories() {
      if (!this.currentPeriodPoints || !this.displacedPeriodPoints) {
        return [];
      }

      const categories = [];
      const categoriesParser = parseCategories(categories);
      this.currentPeriodPoints.forEach(categoriesParser);
      this.displacedPeriodPoints.forEach(categoriesParser);

      return categories;
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
    ]),
    async getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      await this.getChartsService({
        itemId: this.itemId,
        method: 'getDataWithDisplaceRange',
        entity: ENTITIES.USERS,
        points_parser: '',
        params: {
          fields: [USERS_FIELDS.FOLLOWERS],
          grouping: [USERS_FIELDS.CHANNEL, USERS_FIELDS.UID, USERS_FIELDS.USERNAME],
          operators: OPERATION.LAST,
        },
      })
        .then(response => {
          this.rawData = response;
          this.requestStatus = 'success';
        })
        .catch(error => {
          console.error(error);
          this.requestStatus = 'error';
        });
    },
    maxItems() {
      return this.maxItemsToBeDisplayed;
    },
  },
};
</script>
